// src/pages/InventoryUsagePage.jsx
import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaPlus, FaTrash, FaSave, FaSyncAlt, FaCalendarAlt, FaEdit } from "react-icons/fa";
import { inventoryApi } from "../utils/inventoryApi";
import { formatStoredMoney } from "../utils/currency";
import { OFFLINE_KEYS, enqueueOffline, flushQueue } from "../utils/inventoryOfflineQueue";

const COLOR = {
  primary: "#015b9a",
  bg: "#f5f7fb",
  card: "#ffffff",
  line: "#e5e7eb",
  text: "#0f172a",
  muted: "#6b7280",
  ok: "#059669",
  danger: "#dc2626",
};

const createLine = () => ({
  key: `${Date.now()}-${Math.random()}`,
  item_id: "",
  qty: "",
  uom: "",
  note: "",
});
const BASE_UOM_OPTIONS = ["kg", "g", "ltr", "ml", "pcs"];
const OTHER_UOM_VALUE = "__other__";
const normalizeUom = (uom) => String(uom || "").trim().toLowerCase();
const buildUomOptions = (item) => {
  const set = new Set(BASE_UOM_OPTIONS);
  const base = normalizeUom(item?.base_uom);
  if (base) set.add(base);
  if (Array.isArray(item?.allowed_uoms)) {
    item.allowed_uoms.forEach((uom) => {
      const normalized = normalizeUom(uom);
      if (normalized) set.add(normalized);
    });
  }
  return Array.from(set);
};
const decimalText = (value) => {
  if (value === null || value === undefined || value === "") return "";
  return String(value);
};
const buildUsageEditState = (entry) => ({
  id: entry.id,
  item_id: entry.item_id ? String(entry.item_id) : "",
  item_name: entry.item_name || entry.item?.name || "",
  item: entry.item || null,
  date: entry.date || today(),
  qty: decimalText(entry.qty),
  uom: entry.uom || "",
  note: entry.note || "",
  can_delete: Boolean(entry.can_delete),
});

const InventoryUsagePage = () => {
  const [userId, setUserId] = useState(null);
  const [date, setDate] = useState(today());
  const [lines, setLines] = useState([createLine()]);
  const [items, setItems] = useState([]);
  const [recentItems, setRecentItems] = useState([]);
  const [postedUsageEntries, setPostedUsageEntries] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [isConnected, setIsConnected] = useState(
    typeof navigator === "undefined" ? true : navigator.onLine
  );
  const [showTodayOnly, setShowTodayOnly] = useState(true);
  const [ledgerItem, setLedgerItem] = useState(null);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [ledgerEntries, setLedgerEntries] = useState([]);
  const [ledgerMeta, setLedgerMeta] = useState({
    opening_balance: 0,
    closing_balance: 0,
  });
  const [ledgerRange, setLedgerRange] = useState(7);
  const [editingEntry, setEditingEntry] = useState(null);
  const [entrySubmitting, setEntrySubmitting] = useState(false);
  const [deletingEntryId, setDeletingEntryId] = useState(null);

  useEffect(() => {
    const data = JSON.parse(localStorage.getItem("userData") || "{}")?.data;
    if (!data?.id) {
      toast.error("User not found. Please login again.");
      setLoading(false);
      return;
    }
    setUserId(data.id);
  }, []);

  useEffect(() => {
    if (!userId) return;
    loadItems();
  }, [userId, date]);

  useEffect(() => {
    if (!userId) return undefined;

    const syncQueued = async () => {
      const pushed = await flushQueue(OFFLINE_KEYS.STOCK_OUT, inventoryApi.stockOut);
      if (pushed) {
        toast.success(`Synced ${pushed} offline usage entr${pushed > 1 ? "ies" : "y"}`);
        loadItems();
      }
    };

    const onOnline = () => {
      setIsConnected(true);
      syncQueued();
    };
    const onOffline = () => setIsConnected(false);

    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);

    if (navigator.onLine) syncQueued();

    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, [userId]);

  const loadItems = async () => {
    try {
      setLoading(true);
      const [itemsRes, summaryRes, recentRes, stockOutEntriesRes] = await Promise.all([
        inventoryApi.getItems({ restaurantid: userId }),
        inventoryApi.getSummary({ restaurantid: userId, date }),
        inventoryApi.getItems({ restaurantid: userId, recent: true }),
        inventoryApi.getStockOutEntries({ restaurantid: userId, date }),
      ]);
      setItems(itemsRes?.items || []);
      setSummary(summaryRes?.summary || null);
      setRecentItems((recentRes?.items || []).slice(0, 8));
      setPostedUsageEntries(stockOutEntriesRes?.entries || []);
    } catch (e) {
      toast.error(e.message || "Unable to load items");
    } finally {
      setLoading(false);
    }
  };

  const dayLocked = summary?.status === "closed" && date !== today();

  const updateLine = (key, field, value) => {
    setLines((prev) => prev.map((l) => (l.key === key ? { ...l, [field]: value } : l)));
  };
  const updateLineItem = (key, itemId) => {
    const selectedItem = (items || []).find((it) => String(it.id) === String(itemId));
    setLines((prev) =>
      prev.map((line) =>
        line.key === key
          ? {
              ...line,
              item_id: itemId,
              uom: selectedItem?.base_uom || line.uom || "",
            }
          : line
      )
    );
  };
  const addLine = () => setLines((prev) => [...prev, createLine()]);
  const removeLine = (key) =>
    setLines((prev) => (prev.length === 1 ? prev : prev.filter((l) => l.key !== key)));

  const quickSelectItem = (item) => {
    if (dayLocked) {
      toast.info("Closed day can’t be edited. Select today to add usage.");
      return;
    }
    setLines((prev) => {
      const clone = [...prev];
      const targetIndex = clone.findIndex((line) => !line.item_id);
      const nextLine = {
        ...(clone[targetIndex] || createLine()),
        item_id: String(item.id),
        uom: item.base_uom || "",
      };
      if (targetIndex >= 0) clone[targetIndex] = nextLine;
      else clone.push(nextLine);
      return clone;
    });
  };

  const mergedLines = useMemo(() => {
    const valid = lines.filter((l) => l.item_id && Number(l.qty));
    const map = {};
    valid.forEach((l) => {
      const k = `${l.item_id}-${l.uom || ""}`;
      const qty = Number(l.qty) || 0;
      if (!map[k]) map[k] = { ...l, qty };
      else map[k].qty += qty;
    });
    return Object.values(map);
  }, [lines]);

  const handleSave = async () => {
    if (!userId) return;
    if (dayLocked) {
      toast.info("Closed day can’t be edited. Select today to add usage.");
      return;
    }
    if (!mergedLines.length) {
      toast.info("Add at least one item with quantity");
      return;
    }
    const payload = {
      restaurantid: userId,
      user_id: userId,
      date,
      lines: mergedLines.map((l) => ({
        item_id: l.item_id,
        qty_used: Number(l.qty),
        uom: l.uom || undefined,
        note: l.note || undefined,
      })),
    };
    if (!isConnected) {
      await enqueueOffline(OFFLINE_KEYS.STOCK_OUT, payload);
      toast.info("Usage stored offline. It will sync automatically once internet returns.");
      setLines([createLine()]);
      return;
    }
    try {
      setSaving(true);
      const response = await inventoryApi.stockOut(payload);
      setSummary(response?.summary || null);
      toast.success("Usage saved");
      setLines([createLine()]);
      await loadItems();
    } catch (e) {
      toast.error(e.message || "Unable to save usage");
    } finally {
      setSaving(false);
    }
  };

  const filteredSummary = useMemo(() => {
    if (!summary?.items?.length) return [];
    if (!showTodayOnly) return summary.items;
    return summary.items.filter(
      (row) => Number(row?.used || 0) > 0 || Number(row?.in || 0) > 0
    );
  }, [summary, showTodayOnly]);

  const onRefresh = async () => {
    setRefreshing(true);
    await loadItems();
    setRefreshing(false);
  };

  const resolveUsageEntryItem = (entry) =>
    (items || []).find((item) => String(item.id) === String(entry?.item_id)) || entry?.item || null;

  const resolveUsageUnitPrice = (entry, item) => {
    const basePrice = Number(entry?.avg_cost_snapshot);
    if (!Number.isFinite(basePrice)) return null;

    const normalizedUom = normalizeUom(entry?.uom);
    const factor = Number(item?.conversions?.[normalizedUom]);
    if (Number.isFinite(factor) && factor > 0) {
      return basePrice * factor;
    }
    if (normalizedUom && normalizedUom === normalizeUom(item?.base_uom)) {
      return basePrice;
    }
    return basePrice;
  };

  const openEditEntry = (entry) => {
    if (!isConnected) {
      toast.info("Reconnect to edit posted usage entries.");
      return;
    }
    if (!entry?.can_edit) {
      toast.info(entry?.edit_block_reason || "This usage entry cannot be edited.");
      return;
    }
    setEditingEntry(buildUsageEditState(entry));
  };

  const updateEditingEntry = (field, value) => {
    setEditingEntry((prev) => (prev ? { ...prev, [field]: value } : prev));
  };

  const submitEntryEdit = async () => {
    if (!userId || !editingEntry?.id) return;
    if (!isConnected) {
      toast.info("Reconnect to update posted usage entries.");
      return;
    }

    const qtyValue = Number(editingEntry.qty);
    const nextUom = normalizeUom(editingEntry.uom);
    if (!qtyValue) {
      toast.info("Enter a valid quantity");
      return;
    }
    if (!nextUom) {
      toast.info("Select a unit");
      return;
    }

    try {
      setEntrySubmitting(true);
      const response = await inventoryApi.updateStockOutEntry(editingEntry.id, {
        restaurantid: userId,
        date: editingEntry.date,
        qty_used: qtyValue,
        uom: nextUom,
        note: editingEntry.note.trim() || undefined,
      });
      setSummary(response?.summary || null);
      setEditingEntry(null);
      toast.success("Usage entry updated");
      await loadItems();
    } catch (e) {
      toast.error(e.message || "Unable to update usage entry");
    } finally {
      setEntrySubmitting(false);
    }
  };

  const handleDeletePostedUsageEntry = async (entry) => {
    if (!userId || !entry?.id) return;
    if (!isConnected) {
      toast.info("Reconnect to delete posted usage entries.");
      return;
    }
    if (!entry.can_delete) {
      toast.info(entry.edit_block_reason || "This usage entry cannot be deleted.");
      return;
    }
    if (!window.confirm("Delete this usage entry?")) return;

    try {
      setDeletingEntryId(entry.id);
      const response = await inventoryApi.deleteStockOutEntry({
        entryId: entry.id,
        restaurantid: userId,
      });
      setSummary(response?.summary || null);
      setEditingEntry((prev) => (prev?.id === entry.id ? null : prev));
      toast.success("Usage entry deleted");
      await loadItems();
    } catch (e) {
      toast.error(e.message || "Unable to delete usage entry");
    } finally {
      setDeletingEntryId(null);
    }
  };

  const openLedger = async (itemId) => {
    if (!userId || !itemId) return;
    const item = (items || []).find((entry) => String(entry.id) === String(itemId));
    if (!item) return;
    setLedgerItem(item);
    setLedgerLoading(true);
    setLedgerEntries([]);
    try {
      const to = new Date();
      const from = new Date();
      from.setDate(to.getDate() - ledgerRange);
      const response = await inventoryApi.getLedger({
        restaurantid: userId,
        item_id: item.id,
        from: toYMD(from),
        to: toYMD(to),
      });
      setLedgerEntries(response?.entries || []);
      setLedgerMeta({
        opening_balance: response?.opening_balance ?? 0,
        closing_balance: response?.closing_balance ?? 0,
      });
    } catch (error) {
      toast.error(error?.message || "Unable to load ledger");
    } finally {
      setLedgerLoading(false);
    }
  };

  useEffect(() => {
    if (ledgerItem?.id) {
      openLedger(ledgerItem.id);
    }
  }, [ledgerRange]);

  const editingItem = resolveUsageEntryItem(editingEntry);
  const editingUomOptions = buildUomOptions(editingItem);
  const normalizedEditingUom = normalizeUom(editingEntry?.uom);
  const hasCustomEditingUom =
    normalizedEditingUom && !editingUomOptions.includes(normalizedEditingUom);
  const editingUomValue = hasCustomEditingUom ? OTHER_UOM_VALUE : normalizedEditingUom;

  return (
    <Page>
      <ToastContainer position="top-right" autoClose={2200} />
      <Top>
        <div>
          <Title>Inventory — Usage</Title>
          <Sub>Log end-of-day consumption</Sub>
        </div>
        <Actions>
          <StatusPill $online={isConnected}>
            {isConnected ? "Online" : "Offline"}
          </StatusPill>
          <StatusPill $closed={summary?.status === "closed"}>
            {summary?.status === "closed" ? "Closed" : "Open"}
          </StatusPill>
          <DateInput>
            <FaCalendarAlt />
            <input
              type="date"
              max={today()}
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </DateInput>
          <PrimaryBtn onClick={onRefresh} disabled={refreshing}>
            <FaSyncAlt /> {refreshing ? "Refreshing..." : "Refresh Items"}
          </PrimaryBtn>
        </Actions>
      </Top>

      <Card>
        {loading ? (
          <Center>Loading items…</Center>
        ) : (
          <LineList>
            {lines.map((line, idx) => {
              const options = items || [];
              const selectedItem = options.find((it) => String(it.id) === String(line.item_id));
              const uomOptions = buildUomOptions(selectedItem);
              const normalizedLineUom = normalizeUom(line.uom);
              const isCustomUom = normalizedLineUom && !uomOptions.includes(normalizedLineUom);
              const uomValue = isCustomUom ? OTHER_UOM_VALUE : normalizedLineUom;
              return (
                <LineCard key={line.key}>
                  <LineHeader>
                    <span>Line {idx + 1}</span>
                    <button
                      onClick={() => removeLine(line.key)}
                      disabled={lines.length === 1 || dayLocked}
                    >
                      <FaTrash /> Remove
                    </button>
                  </LineHeader>
                  <LineGrid>
                    <LineField>
                      <SmallLabel>Item</SmallLabel>
                      <Select
                        value={line.item_id}
                        disabled={dayLocked}
                        onChange={(e) => updateLineItem(line.key, e.target.value)}
                      >
                        <option value="">Select item</option>
                        {options.map((it) => (
                          <option key={it.id} value={it.id}>
                            {it.name}
                          </option>
                        ))}
                      </Select>
                    </LineField>
                    <LineField>
                      <SmallLabel>Qty Used</SmallLabel>
                      <Input
                        value={line.qty}
                        disabled={dayLocked}
                        onChange={(e) =>
                          updateLine(line.key, "qty", e.target.value.replace(/[^\d.]/g, ""))
                        }
                        placeholder="0"
                        inputMode="decimal"
                        pattern="[0-9.]*"
                        maxLength={8}
                      />
                    </LineField>
                    <LineField>
                      <SmallLabel>UOM</SmallLabel>
                      <Select
                        value={uomValue}
                        disabled={dayLocked}
                        onChange={(e) => {
                          const next = e.target.value;
                          if (next === OTHER_UOM_VALUE) {
                            if (!isCustomUom) updateLine(line.key, "uom", "");
                            return;
                          }
                          updateLine(line.key, "uom", next);
                        }}
                      >
                        <option value="">Select UOM</option>
                        {uomOptions.map((uom) => (
                          <option key={uom} value={uom}>
                            {uom.toUpperCase()}
                          </option>
                        ))}
                        <option value={OTHER_UOM_VALUE}>Other</option>
                      </Select>
                      {(uomValue === OTHER_UOM_VALUE || isCustomUom) && (
                        <Input
                          style={{ marginTop: 8 }}
                          disabled={dayLocked}
                          value={line.uom}
                          onChange={(e) => updateLine(line.key, "uom", normalizeUom(e.target.value))}
                          placeholder="Enter custom UOM"
                        />
                      )}
                    </LineField>
                    <LineField>
                      <SmallLabel>Note</SmallLabel>
                      <Input
                        value={line.note}
                        disabled={dayLocked}
                        onChange={(e) => updateLine(line.key, "note", e.target.value)}
                        placeholder="optional"
                      />
                    </LineField>
                  </LineGrid>
                </LineCard>
              );
            })}
          </LineList>
        )}

        <Actions>
          <GhostBtn onClick={addLine} disabled={dayLocked}>
            <FaPlus /> Add Line
          </GhostBtn>
          <PrimaryBtn onClick={handleSave} disabled={saving || dayLocked}>
            <FaSave /> {saving ? "Saving..." : "Save Usage"}
          </PrimaryBtn>
        </Actions>
      </Card>

      <Card style={{ marginTop: 12 }}>
        <CardHead>
          <CardTitle>Posted Usage</CardTitle>
          <SummaryMeta>{date}</SummaryMeta>
        </CardHead>
        {loading ? (
          <Center>Loading posted usage…</Center>
        ) : postedUsageEntries.length ? (
          <SummaryList>
            {postedUsageEntries.map((entry) => {
              const entryItem = resolveUsageEntryItem(entry);
              const isDeleting = deletingEntryId === entry.id;
              const usageUnitPrice = resolveUsageUnitPrice(entry, entryItem);
              return (
                <PostedEntryCard key={entry.id}>
                  <PostedEntryHeader>
                    <div>
                      <SummaryName>{entry.item_name || entryItem?.name || "Used"}</SummaryName>
                      <SummaryMeta>
                        {entry.date} • -{entry.qty} {String(entry.uom || "").toUpperCase()}
                        {usageUnitPrice !== null
                          ? ` • ${formatStoredMoney(usageUnitPrice)}/${String(entry.uom || "").toUpperCase()}`
                          : ""}
                      </SummaryMeta>
                    </div>
                    <PostedEntryActions>
                      <IconBtn
                        type="button"
                        title="Edit entry"
                        onClick={() => openEditEntry(entry)}
                        disabled={!entry.can_edit || isDeleting || !isConnected}
                      >
                        <FaEdit />
                      </IconBtn>
                      <IconBtn
                        type="button"
                        title="Delete entry"
                        $danger
                        onClick={() => handleDeletePostedUsageEntry(entry)}
                        disabled={!entry.can_delete || isDeleting || !isConnected}
                      >
                        <FaTrash />
                      </IconBtn>
                    </PostedEntryActions>
                  </PostedEntryHeader>

                  {entry.note ? <SummaryMeta>{entry.note}</SummaryMeta> : null}
                  {entry.source_label ? <SummaryMeta>{entry.source_label}</SummaryMeta> : null}
                  {!entry.can_edit && entry.edit_block_reason ? (
                    <LockedText>{entry.edit_block_reason}</LockedText>
                  ) : null}
                </PostedEntryCard>
              );
            })}
          </SummaryList>
        ) : (
          <Center>No posted usage entries for this date</Center>
        )}
      </Card>

      {recentItems.length ? (
        <Card style={{ marginTop: 12 }}>
          <CardHead>
            <CardTitle>Recent Items</CardTitle>
            <GhostBtn onClick={loadItems}>
              <FaSyncAlt /> Refresh
            </GhostBtn>
          </CardHead>
          <ChipRow>
            {recentItems.map((item) => (
              <ChipBtn key={item.id} onClick={() => quickSelectItem(item)} disabled={dayLocked}>
                {item.name}
              </ChipBtn>
            ))}
          </ChipRow>
        </Card>
      ) : null}

      {filteredSummary.length ? (
        <Card style={{ marginTop: 12 }}>
          <CardHead>
            <CardTitle>Inventory Summary</CardTitle>
            <ToggleWrap>
              <input
                type="checkbox"
                checked={showTodayOnly}
                onChange={(e) => setShowTodayOnly(e.target.checked)}
              />
              <span>Show active today only</span>
            </ToggleWrap>
          </CardHead>
          <SummaryList>
            {filteredSummary.map((row) => (
              <SummaryRow key={row.item_id}>
                <div>
                  <SummaryName>{row.name}</SummaryName>
                  <SummaryMeta>
                    Available: {row.available} {row.base_uom}
                  </SummaryMeta>
                </div>
                <SummaryActions>
                  <SummaryValue>
                    -{row.used} {row.base_uom}
                  </SummaryValue>
                  <GhostBtn type="button" onClick={() => openLedger(row.item_id)}>
                    Ledger
                  </GhostBtn>
                </SummaryActions>
              </SummaryRow>
            ))}
          </SummaryList>
        </Card>
      ) : null}

      {ledgerItem ? (
        <ModalBackdrop onClick={() => setLedgerItem(null)}>
          <ModalCard onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <h3>{ledgerItem.name}</h3>
              <button onClick={() => setLedgerItem(null)}>✕</button>
            </ModalHeader>
            <LedgerRangeRow>
              {[7, 30].map((days) => (
                <RangeBtn
                  key={days}
                  type="button"
                  $active={ledgerRange === days}
                  onClick={() => setLedgerRange(days)}
                >
                  {days}d
                </RangeBtn>
              ))}
            </LedgerRangeRow>
            <LedgerStats>
              <div>
                <SmallLabel>Opening</SmallLabel>
                <LedgerValue>{ledgerMeta.opening_balance}</LedgerValue>
              </div>
              <div>
                <SmallLabel>Closing</SmallLabel>
                <LedgerValue>{ledgerMeta.closing_balance}</LedgerValue>
              </div>
            </LedgerStats>
            {ledgerLoading ? (
              <Center>Loading ledger…</Center>
            ) : ledgerEntries.length ? (
              <SummaryList>
                {ledgerEntries.map((entry, index) => (
                  <SummaryRow key={`${entry.date}-${index}`}>
                    <div>
                      <SummaryName>{entry.date}</SummaryName>
                      <SummaryMeta>
                        {entry.type === "in" ? "Stock In" : "Used"} · {entry.qty} {entry.uom}
                      </SummaryMeta>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <SummaryValue>
                        {entry.type === "in" ? "+" : "-"}
                        {entry.qty}
                      </SummaryValue>
                      <SummaryMeta>Balance {entry.running_balance}</SummaryMeta>
                    </div>
                  </SummaryRow>
                ))}
              </SummaryList>
            ) : (
              <Center>No ledger movement found</Center>
            )}
          </ModalCard>
        </ModalBackdrop>
      ) : null}

      {editingEntry ? (
        <ModalBackdrop onClick={() => { if (!entrySubmitting) setEditingEntry(null); }}>
          <ModalCard onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <h3>Edit Usage Entry</h3>
              <button onClick={() => { if (!entrySubmitting) setEditingEntry(null); }}>
                ✕
              </button>
            </ModalHeader>
            <Field>
              <Label>Item</Label>
              <Input value={editingEntry.item_name || editingItem?.name || ""} disabled />
            </Field>
            <Inline>
              <Field style={{ flex: 1 }}>
                <Label>Date</Label>
                <Input
                  type="date"
                  max={today()}
                  value={editingEntry.date}
                  onChange={(e) => updateEditingEntry("date", e.target.value)}
                  disabled={entrySubmitting}
                />
              </Field>
              <Field style={{ flex: 1 }}>
                <Label>Quantity</Label>
                <Input
                  value={editingEntry.qty}
                  onChange={(e) =>
                    updateEditingEntry("qty", e.target.value.replace(/[^\d.]/g, ""))
                  }
                  placeholder="0"
                  inputMode="decimal"
                  disabled={entrySubmitting}
                />
              </Field>
            </Inline>
            <Field>
              <Label>UOM</Label>
              <Select
                value={editingUomValue}
                onChange={(e) => {
                  const next = e.target.value;
                  if (next === OTHER_UOM_VALUE) {
                    if (!hasCustomEditingUom) updateEditingEntry("uom", "");
                    return;
                  }
                  updateEditingEntry("uom", next);
                }}
                disabled={entrySubmitting}
              >
                <option value="">Select UOM</option>
                {editingUomOptions.map((uom) => (
                  <option key={uom} value={uom}>
                    {String(uom).toUpperCase()}
                  </option>
                ))}
                <option value={OTHER_UOM_VALUE}>Other</option>
              </Select>
              {(editingUomValue === OTHER_UOM_VALUE || hasCustomEditingUom) && (
                <Input
                  style={{ marginTop: 8 }}
                  value={editingEntry.uom}
                  onChange={(e) => updateEditingEntry("uom", normalizeUom(e.target.value))}
                  placeholder="Enter custom UOM"
                  disabled={entrySubmitting}
                />
              )}
            </Field>
            <Field>
              <Label>Note (optional)</Label>
              <Input
                value={editingEntry.note}
                onChange={(e) => updateEditingEntry("note", e.target.value)}
                placeholder="optional"
                disabled={entrySubmitting}
              />
            </Field>
            <Inline style={{ justifyContent: "space-between", flexWrap: "wrap" }}>
              <Inline style={{ gap: 8, flexWrap: "wrap" }}>
                <GhostBtn
                  type="button"
                  onClick={() => setEditingEntry(null)}
                  disabled={entrySubmitting}
                >
                  Close
                </GhostBtn>
                {editingEntry.can_delete ? (
                  <DangerBtn
                    type="button"
                    onClick={() => handleDeletePostedUsageEntry(editingEntry)}
                    disabled={entrySubmitting || deletingEntryId === editingEntry.id}
                  >
                    <FaTrash /> Delete
                  </DangerBtn>
                ) : null}
              </Inline>
              <PrimaryBtn type="button" onClick={submitEntryEdit} disabled={entrySubmitting}>
                <FaSave /> {entrySubmitting ? "Saving..." : "Save Changes"}
              </PrimaryBtn>
            </Inline>
          </ModalCard>
        </ModalBackdrop>
      ) : null}
    </Page>
  );
};

export default InventoryUsagePage;

const today = () => {
  const d = new Date();
  const pad = (v) => String(v).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const toYMD = (dateValue) => {
  const d = new Date(dateValue);
  const pad = (v) => String(v).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

/* ---------------- styled ---------------- */
const Page = styled.div`
  background: ${COLOR.bg};
  min-height: 100vh;
  padding: 16px;
`;
const Top = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 12px;
`;
const Title = styled.h1`
  margin: 0;
  font-size: 1.2rem;
  font-weight: 900;
  color: ${COLOR.text};
`;
const Sub = styled.div`
  color: ${COLOR.muted};
  font-size: 0.95rem;
`;
const Actions = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
`;
const StatusPill = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 8px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 800;
  border: 1px solid
    ${({ $online, $closed }) =>
      $closed ? "#f59e0b" : $online === false ? "#c2410c" : "#0f766e"};
  color: ${({ $online, $closed }) =>
    $closed ? "#92400e" : $online === false ? "#9a3412" : "#115e59"};
  background: ${({ $online, $closed }) =>
    $closed ? "#fef3c7" : $online === false ? "#ffedd5" : "#ecfeff"};
`;
const DateInput = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 10px;
  border: 1px solid ${COLOR.line};
  border-radius: 10px;
  background: #f9f9f9;
  color: ${COLOR.text};
  input {
    border: 0;
    background: transparent;
    outline: none;
  }
`;

const Card = styled.div`
  background: ${COLOR.card};
  border: 1px solid ${COLOR.line};
  border-radius: 16px;
  padding: 14px;
  box-shadow: 0 10px 26px rgba(0, 0, 0, 0.05);
`;
const CardHead = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
`;
const CardTitle = styled.div`
  font-weight: 800;
  color: ${COLOR.text};
`;
const Field = styled.div`
  display: grid;
  gap: 6px;
`;
const Label = styled.label`
  font-weight: 800;
  color: ${COLOR.text};
`;
const Input = styled.input`
  border: 1px solid ${COLOR.line};
  border-radius: 10px;
  padding: 10px 12px;
  background: #f9f9f9;
  color: ${COLOR.text};
  outline: none;
`;
const Select = styled.select`
  border: 1px solid ${COLOR.line};
  border-radius: 10px;
  padding: 10px 12px;
  background: #f9f9f9;
  color: ${COLOR.text};
  outline: none;
`;
const PrimaryBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 12px;
  border-radius: 10px;
  border: none;
  background: ${COLOR.primary};
  color: #fff;
  font-weight: 800;
  cursor: pointer;
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;
const GhostBtn = styled(PrimaryBtn)`
  background: #eef6ff;
  color: ${COLOR.primary};
  border: 1px solid ${COLOR.primary};
`;
const LineList = styled.div`
  display: grid;
  gap: 10px;
  margin-top: 6px;
`;
const LineCard = styled.div`
  border: 1px solid ${COLOR.line};
  border-radius: 12px;
  padding: 10px;
  background: #f8fafc;
`;
const LineHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
  button {
    border: 1px solid #dc2626;
    color: #dc2626;
    background: #fff5f5;
    border-radius: 10px;
    padding: 6px 8px;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    cursor: pointer;
    &:disabled {
      opacity: 0.45;
      cursor: not-allowed;
    }
  }
`;
const LineGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 2fr;
  gap: 8px;
  @media (max-width: 960px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;
const LineField = styled.div`
  display: grid;
  gap: 4px;
`;
const SmallLabel = styled.div`
  font-size: 12px;
  color: ${COLOR.muted};
`;
const Center = styled.div`
  text-align: center;
  color: ${COLOR.muted};
  padding: 16px;
`;
const SummaryList = styled.div`
  display: grid;
  gap: 8px;
`;
const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 10px;
  border: 1px solid ${COLOR.line};
  border-radius: 10px;
  background: #f8fafc;
`;
const SummaryName = styled.div`
  font-weight: 700;
  color: ${COLOR.text};
`;
const SummaryMeta = styled.div`
  font-size: 12px;
  color: ${COLOR.muted};
`;
const SummaryValue = styled.div`
  font-weight: 700;
  color: ${COLOR.primary};
`;
const PostedEntryCard = styled.div`
  border: 1px solid ${COLOR.line};
  border-radius: 12px;
  padding: 12px;
  background: #f8fafc;
  display: grid;
  gap: 6px;
`;
const PostedEntryHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
`;
const PostedEntryActions = styled.div`
  display: inline-flex;
  gap: 8px;
  flex-wrap: wrap;
`;
const IconBtn = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  border: 1px solid ${({ $danger }) => ($danger ? "#fecaca" : "#bfdbfe")};
  background: ${({ $danger }) => ($danger ? "#fff1f2" : "#eff6ff")};
  color: ${({ $danger }) => ($danger ? COLOR.danger : COLOR.primary)};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;
const LockedText = styled.div`
  color: ${COLOR.danger};
  font-size: 13px;
  font-weight: 600;
`;
const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;
const ChipBtn = styled.button`
  padding: 8px 12px;
  border-radius: 999px;
  border: 1px solid ${COLOR.primary};
  background: #fff;
  color: ${COLOR.primary};
  font-weight: 700;
  cursor: pointer;
  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;
const ToggleWrap = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: ${COLOR.muted};
  font-weight: 600;
`;
const SummaryActions = styled.div`
  display: grid;
  gap: 6px;
  justify-items: end;
`;
const ModalBackdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.4);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding: 16px;
  z-index: 50;
`;
const ModalCard = styled.div`
  background: #fff;
  border-radius: 16px;
  padding: 16px;
  width: min(560px, 100%);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
  display: grid;
  gap: 12px;
  max-height: 85vh;
  overflow: auto;
`;
const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  h3 {
    margin: 0;
  }
  button {
    border: none;
    background: transparent;
    font-size: 18px;
    cursor: pointer;
  }
`;
const Inline = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-start;
`;
const LedgerRangeRow = styled.div`
  display: flex;
  gap: 8px;
`;
const RangeBtn = styled.button`
  border: 1px solid ${({ $active }) => ($active ? COLOR.primary : COLOR.line)};
  background: ${({ $active }) => ($active ? COLOR.primary : "#fff")};
  color: ${({ $active }) => ($active ? "#fff" : COLOR.text)};
  border-radius: 10px;
  padding: 8px 12px;
  font-weight: 700;
  cursor: pointer;
`;
const LedgerStats = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 12px;
  border: 1px solid ${COLOR.line};
  border-radius: 12px;
  background: #f8fbff;
`;
const LedgerValue = styled.div`
  font-size: 16px;
  font-weight: 800;
  color: ${COLOR.text};
`;
const DangerBtn = styled(PrimaryBtn)`
  background: ${COLOR.danger};
  color: #fff;
`;
